import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { TRADING_ELOQUENT as ELOQUENT } from 'config/constants';
import { queryClient, queryClientKeys } from 'config/executors/apiInterceptor';
import { useMemo } from 'react';

/**
 * Get logged in user's profile data
 * @param {Object} data
 * @returns {Promise}
 */
export function getProductTypes(data, { token }) {
    return ELOQUENT.ProductTypes.index(data, token);
}

export function useGetProductTypeInfinite(apiCaller, initialPageParam, enabled, isFreight) {
    const api = useInfiniteQuery({
        queryKey: [isFreight ? queryClientKeys.useGetProductTypeInfiniteFreight : queryClientKeys.useGetProductTypeInfinite, initialPageParam],
        initialPageParam: initialPageParam,
        enabled,
        queryFn: (ee) => {
            const dataF = ee.queryKey[1];

            return apiCaller(getProductTypes, { ...dataF, ...ee.pageParam });
        },
        getNextPageParam: (lastPage, _, lastPageParam) => {
            if (!lastPage?.data?.next_page_url) return undefined;

            return {
                ...lastPageParam,
                page: lastPage?.data?.next_page_url
                    ? lastPage?.data?.current_page + 1
                    : lastPage?.data?.current_page
            };
        }
    });

    const finalData = useMemo(() => {
        return api.data?.pages.reduce((prev, cur) => {
            return [...prev, ...cur.data.data];
        }, []) ?? [];
    }, [api.data]);

    const pageData = useMemo(() => {
        const currentPage = api.data?.pages[0].data;

        return currentPage ?? {
            data: [],
            per_page: initialPageParam.per_page ?? 30,
            total: initialPageParam.total ?? 0,
            current_page: 1
        };
    }, [api.data?.pages, initialPageParam.per_page, initialPageParam.total]);

    return {
        ...api,
        data: finalData,
        pageData
    };
}

export function useGetSearchableProductTypeInfinite(apiCaller, initialPageParam, format) {
    const api = useInfiniteQuery({
        queryKey: [queryClientKeys.useGetSearchableProductTypeInfinite, initialPageParam],
        initialPageParam: initialPageParam,
        queryFn: (ee) => {
            const dataF = ee.queryKey[1];

            return apiCaller(getProductTypes, { ...dataF, ...ee.pageParam });
        },
        getNextPageParam: (lastPage, _, lastPageParam) => {
            if (!lastPage?.data?.next_page_url) return undefined;

            return {
                ...lastPageParam,
                page: lastPage?.data?.next_page_url
                    ? lastPage?.data?.current_page + 1
                    : lastPage?.data?.current_page
            };
        }
    });

    const finalData = useMemo(() => {
        const mergedPages = api.data?.pages.reduce((prev, cur) => {
            return [...prev, ...cur.data.data];
        }, []) ?? [];

        return format ? format(mergedPages) : mergedPages;
    }, [api.data, format]);

    return { ...api, data: finalData };
}

/**
 * Get all tenors
 * @param {Object} data
 * @returns {Promise}
 */
export function requesRoamingProductAccess(data, { token }) {
    return ELOQUENT.ProductTypes.tradeRequest(data, token);
}

export const useRequesRoamingProductAccessApi = (apiCaller) => {
    return useMutation({
        mutationFn: async productId => await apiCaller(requesRoamingProductAccess, productId)
    });
};

/**
 * Get all tenors
 * @param {Object} data
 * @returns {Promise}
 */
export function getAllTenors(data, { token }) {
    return ELOQUENT.ProductTypes.tenors(data, token);
}

export const invalidateGetProductTypeTenors = () => {
    queryClient.invalidateQueries([queryClientKeys.useGetProductTypeTenors]);
};

export function useGetProductTypeTenors(apiCaller, initialPageParam) {
    const api = useQuery({
        queryKey: [queryClientKeys.useGetProductTypeTenors, initialPageParam],
        queryFn: async ee => await apiCaller(getAllTenors, initialPageParam)
    });

    const dataList = useMemo(() => api.data?.data ?? [], [api.data]);

    return {
        ...api,
        dataList
    };
}

/**
 * Get products without pagination
 * @param {Object} data
 * @returns {Promise}
 */
export function getAllProductTypes(data, { token }) {
    return ELOQUENT.ProductTypes.all(data, token);
}

export function useGetAllProductTypes(apiCaller, initialPageParam, mapper) {
    const api = useQuery({
        queryKey: [queryClientKeys.useGetProductTypeTenors, initialPageParam],
        queryFn: async ee => await apiCaller(getAllProductTypes, initialPageParam)
    });

    return {
        ...api,
        data: mapper ? mapper(api.data?.data) : (api.data?.data ?? [])
    };
}

/**
 * Get products with pagination
 * @param {Object} data
 * @returns {Promise}
 */
export function getPaginatedProductTypes(data, { token }) {
    return ELOQUENT.ProductTypes.index(data, token);
}

/**
 * Create product type
 * @param {Object} data
 * @returns {Promise}
 */
export function createProductTypes(data, { token }) {
    return ELOQUENT.ProductTypes.create(data, token);
}

/**
 * Update product type
 * @param {Number} id
 * @param {Object} data
 * @returns {Promise}
 */
export function updateProductTypes(id, data, { token }) {
    return ELOQUENT.ProductTypes.update(id, data, token);
}

/**
 * Update status of product type
 * @param {Number} id
 * @param {Object} data
 * @returns {Promise}
 */
export function updateStatusProductTypes(id, data, { token }) {
    return ELOQUENT.ProductTypes.updateStatus(id, data, token);
}

/**
 * Create indicative orders upload template.
 * @param {Object} data : {product_type_ids: integer[] }
 * @returns {Promise}
 */
function generateIndicativeOrdersUploadTemplte(data, { token }) {
    return ELOQUENT.BulkIndicativeOrders.createUploadTemplate(data, token);
}

export const useGenerateIndicativeOrdersUploadTemplteApi = (apiCaller) => {
    return useMutation({
        mutationFn: async (data) => await apiCaller(generateIndicativeOrdersUploadTemplte, data)
    });
};

/**
 * Create indicative orders upload template.
 * @param {Object} data : {
 *  key: string,
 * trader_id?: integer, // required if account is broker
 * is_visible?: boolean,
 * is_gtc?: boolean
 * }
 * @returns {Promise}
 */
function uploadIndicativeOrdersUploadTemplte(data, { token }) {
    return ELOQUENT.BulkIndicativeOrders.bulkUpload(data, token);
}

export const useUploadIndicativeOrdersUploadTemplteApi = (apiCaller) => {
    return useMutation({
        mutationFn: async (data) => await apiCaller(uploadIndicativeOrdersUploadTemplte, data)
    });
};
